import { useTheme } from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { ItemCallback, Layout, Responsive as ResponsiveGrid, WidthProvider } from 'react-grid-layout';
import useWindowSize from '../../hooks/useWindowSize';

import { moveArrayElements } from '../../utils/helpers';

import './DraggableList.module.scss';

interface IDraggableListProps<T> {
  items: T[];
  render: React.ReactNode;
  onDragStop: (items: T[]) => void;
  /** Returns the unique key for each item; required so `react-grid-layout` can track rows across drags. */
  getId: (item: T) => string;
  readOnly?: boolean;
  rowHeight?: number;
  isBounded?: boolean;
}

const Responsive = WidthProvider(ResponsiveGrid);

// prettier-ignore
const DraggableList = <T,>({ items, render, readOnly, onDragStop, rowHeight, getId, isBounded = true }: IDraggableListProps<T>) => {
  const { sass: variables } = useTheme();
  const { HD, mobile } = useWindowSize();

  const responsiveRowHeight = useMemo(() => {
    switch (true) {
      case mobile:
        return 134;
      case HD:
        return 150;
      default:
        return 160;
    }
  }, [mobile, HD]);

  const height = rowHeight ?? responsiveRowHeight;

  const layout: Layout[] = useMemo(
    () =>
      items.map((item, index) => ({
        i: getId(item),
        x: 0,
        y: index,
        w: 1,
        h: 1,
        maxW: 1,
        minW: 1,
        isResizable: false,
      })),
    [items, getId],
  );

  const handleDragStop: ItemCallback = useCallback(
    (currentLayout, { y: oldIndex }, { y: newIndex }) => {
      onDragStop(moveArrayElements(items, oldIndex, newIndex));
    },
    [onDragStop, items],
  );

  return readOnly ? (
    <>{render}</>
  ) : (
    <Responsive
      draggableCancel=".no-drag"
      rowHeight={height}
      breakpoints={{
        lg: +variables.breakpointLargeDesktop,
        md: +variables.breakpointDesktop,
        sm: +variables.breakpointMedium,
        xs: +variables.breakpointMobile,
        xxs: +variables.breakpointMobileMin,
      }}
      cols={{ lg: 1 }}
      layouts={{ lg: layout }}
      margin={[0, 20]}
      isBounded={isBounded}
      onDragStop={handleDragStop}
    >
      {render}
    </Responsive>
  );
};

export default DraggableList;
